import React, { useState } from 'react';
import { usePortalProps } from './ShadowPortalContext';
import { Apps as AppsIcon } from '@mui/icons-material';
import { IconButton, Popover, Paper } from '@mui/material';
import styled from '@emotion/styled';
import { Link, Typography, designTokens } from '@platform-ui/design-system';
import { AppSwitcherData } from './hooks/useAppSwitcherDataTypes';
import AppSwitcherTree from './AppSwitcher/AppSwitcherTree';

interface StyledIconProps {
  $variant?: string;
}

/**
  "APISANDBOX": "API Sandbox",
  "EU_SANDBOX": "API Sandbox",
  "NA_SANDBOX": "API Sandbox",
  "US_ZUORA": "Production",
  "NA_ZUORA": "Production",
  "EU_ZUORA": "Production",
  "US_CENTRAL": "Central Sandbox",
  "EU_CENTRAL": "Central Sandbox",
  "STAGING": "Staging"
 */

const getMenuIconColor = (variant?: string, navShow?: boolean) => {
  if (navShow === false) {
    return designTokens.colors.white;
  }
  if (variant === 'zuora') {
    return designTokens.colors.teal300;
  }
  return designTokens.colors.white;
};

export const StyledAppsCloseIcon = styled(AppsIcon)<StyledIconProps>`
  fill: ${({ $variant }) => getMenuIconColor($variant)};
`;

export const StyledAppsOpenIcon = styled(AppsIcon)<StyledIconProps>`
  fill: ${({ $variant }) => getMenuIconColor($variant)};
`;

const StyledPaper = styled(Paper)({
  width: '328px',
  boxSizing: 'content-box',
  borderRadius: '4px',
  padding: '16px 24px 24px 24px',
  display: 'flex',
  alignItems: 'flex-start',
  background:
    'linear-gradient(0deg, #FFFFFF, #FFFFFF), linear-gradient(0deg, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.12))',
  border: '1px solid #0000001F',
  flexDirection: 'column',
});

const StyledContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  width: '100%',
});

const StyledAccordionsWrapper = styled.div({
  marginTop: '10px',
  width: '100%',
});

export const ZAppsButton = ({
  appSwitcherData,
}: {
  appSwitcherData: AppSwitcherData | undefined;
}) => {
  const [appsPopoverOpen, setAppsPopoverOpen] = useState(false);
  const buildPortalProps = usePortalProps();

  const [anchorEl, setAnchorEl] = useState(null);

  const ResolvedIcon = !appsPopoverOpen
    ? StyledAppsOpenIcon
    : StyledAppsCloseIcon;

  const handleZAppsClick = (event) => {
    if (!appsPopoverOpen) {
      setAppsPopoverOpen(true);
      setAnchorEl(event.currentTarget);
    } else {
      setAppsPopoverOpen(false);
      setAnchorEl(null);
    }
  };

  const getZuoraOneIDUrl = (): string => {
    const hostname = window.location.hostname;

    if (hostname.includes('staging2')) {
      return 'https://one.stg.na.zuora.com/one-id/home';
    } else if (hostname.includes('preprod')) {
      return 'https://one.preprod.zuora.com/one-id/home';
    } else {
      return 'https://one.zuora.com/one-id/home';
    }
  };
  return (
    <>
      <IconButton
        size="large"
        aria-expanded={appsPopoverOpen}
        aria-label="Z Apps Button Panel"
        data-e2e="zapps-popover-toggle"
        onClick={handleZAppsClick}
      >
        <ResolvedIcon
          style={{
            fill: appsPopoverOpen
              ? designTokens.colors.teal300
              : designTokens.colors.white,
          }}
        />
      </IconButton>
      {anchorEl && (
        <Popover
          data-e2e="zapps-popover"
          id="zapps-popover"
          open={appsPopoverOpen}
          anchorEl={anchorEl}
          anchorOrigin={{
            horizontal: 'left',
            vertical: 'bottom',
          }}
          onClose={() => setAppsPopoverOpen(false)}
          transformOrigin={{
            horizontal: 'left',
            vertical: 'top',
          }}
          style={{ transform: 'translate(-8px,-10px)' }}
          {...buildPortalProps()}
          aria-haspopup="true"
          aria-expanded={appsPopoverOpen}
          PaperProps={{
            style: { maxHeight: '60vh', overflow: 'auto' },
          }}
        >
          <StyledPaper>
            <StyledContainer>
              <Typography color={designTokens.colors.black} variant="title">
                Z-Apps
              </Typography>
              <Link
                underline="hover"
                href={getZuoraOneIDUrl()}
                color={designTokens.colors.blue500}
                variant="body2"
                target="_blank"
              >
                Zuora OneID
              </Link>
            </StyledContainer>
            <StyledAccordionsWrapper>
              <AppSwitcherTree
                tenants={
                  appSwitcherData?.data?.tenants
                    ? appSwitcherData?.data?.tenants
                    : []
                }
              />
            </StyledAccordionsWrapper>
          </StyledPaper>
        </Popover>
      )}
    </>
  );
};
